import { api, unwrap, type APIResponse, type Paginated } from "./client";

// Mirrors app/routers/destination.py DestinationOut.
export interface Destination {
  destination_id: number;
  name: string;
  prefix: string;
  ocn: string | null;
  country_id: number | null;
  timezone_id: number | null;
}

// Mirrors app/routers/integration.py IntegrationOut.
export interface Integration {
  integration_id: number;
  name: string;
  type: string;
  description: string | null;
}

export async function listDestinations(
  page = 1,
  per_page = 50,
): Promise<Paginated<Destination>> {
  const resp = await api.get<Paginated<Destination>>("/destinations/", {
    params: { page, per_page },
  });
  if (!resp.data.success) throw new Error(resp.data.error || "Failed to load destinations");
  return resp.data;
}

export interface DestinationInput {
  name: string;
  prefix: string;
  ocn?: string | null;
  country_id?: number | null;
  timezone_id?: number | null;
}

export async function createDestination(body: DestinationInput): Promise<number> {
  const resp = await api.post<APIResponse<{ destination_id: number }>>("/destinations/", body);
  return unwrap(resp.data).destination_id;
}

export async function updateDestination(id: number, body: DestinationInput): Promise<void> {
  await api.put(`/destinations/${id}`, body);
}

export async function deleteDestination(id: number): Promise<void> {
  await api.delete(`/destinations/${id}`);
}

export async function listIntegrations(): Promise<Integration[]> {
  const resp = await api.get<APIResponse<Integration[]>>("/integrations/");
  return unwrap(resp.data);
}

export interface IntegrationDetail extends Integration {
  config: string | null;
}

export interface IntegrationInput {
  name: string;
  type: string;
  description?: string | null;
  config?: string | null;
}

export async function getIntegration(id: number): Promise<IntegrationDetail> {
  const resp = await api.get<APIResponse<IntegrationDetail>>(`/integrations/${id}`);
  return unwrap(resp.data);
}

export async function createIntegration(body: IntegrationInput): Promise<number> {
  const resp = await api.post<APIResponse<{ integration_id: number }>>("/integrations/", body);
  return unwrap(resp.data).integration_id;
}

export async function updateIntegration(id: number, body: IntegrationInput): Promise<void> {
  await api.put(`/integrations/${id}`, body);
}

export async function deleteIntegration(id: number): Promise<void> {
  await api.delete(`/integrations/${id}`);
}

// Mirrors app/routers/route.py RouteOut.
export interface Route {
  route_id: number;
  trunk_id: number;
  destination_id: number;
  priority: number;
  weight: number;
}

export async function listRoutes(page = 1, per_page = 50): Promise<Paginated<Route>> {
  const resp = await api.get<Paginated<Route>>("/routes/", { params: { page, per_page } });
  if (!resp.data.success) throw new Error(resp.data.error || "Failed to load routes");
  return resp.data;
}

export interface RouteInput {
  trunk_id: number;
  destination_id: number;
  priority: number;
  weight: number;
}

export async function createRoute(body: RouteInput): Promise<number> {
  const resp = await api.post<APIResponse<{ route_id: number }>>("/routes/", body);
  return unwrap(resp.data).route_id;
}

export async function updateRoute(id: number, body: RouteInput): Promise<void> {
  await api.put(`/routes/${id}`, body);
}

export async function deleteRoute(id: number): Promise<void> {
  await api.delete(`/routes/${id}`);
}
